import { orderBy } from '../../../../../util/lodashlike';

/**
 * Pure option-shaping helpers for the drawer's field dropdowns (FIN-12586).
 *
 * Extracted from Functions.js because that module cannot be imported in a test — it pulls the
 * whole Redux and component tree with it. These four functions are where the Aggregate Field
 * dropdown silently lost its block grouping, so they are exactly the part worth pinning.
 */

// Pre-v3 rows and the movers carry this label; kept in step with trmUtil's blockLabelTrail.
const GENERAL_GROUP = 'General';

// Group headers read as a breadcrumb: "Transaction Event > Card Context".
const PATH_SEPARATOR = ' › ';

export const formatFieldsForDropdown = (fieldData) => {
  // FIN-11825: native Object.entries throws on undefined/null. fieldData can be the empty
  // result of getApplicableFields when applicableTo does not match any of the field bucket
  // keys — match lodash-style null tolerance so the dropdown renders empty instead of crashing.
  const options = Object.entries(fieldData || {})
    // FIN-13223 — the config carries a hidden legacy-spelling twin of every renamed field so old
    // rules still resolve. It is the same field, so listing it here would double the dropdown.
    .filter(([, value]) => !value?.hideForSelect)
    .map(([key, value]) => ({
      value: key,
      label: value.label,
      processingType: value.processingType,
      // The BE-seeded `fieldList` allowlist below is still spelled the legacy way.
      legacyName: value.legacyName,
      // The same v3 block trail the rule-builder picker groups by.
      blockTrail: value.blockTrail
    }));

  return orderBy(options, 'label', 'asc') || [];
};

/**
 * The rendered option shape.
 *
 * The projection keeps `processingType` out of the options while carrying `blockTrail` through.
 * Dropping the trail here is what left Aggregate Field ungrouped while Group By — which passes
 * `isFields=false` and hands the objects through untouched — grouped correctly.
 */
const pickFieldOption = ({ label, value, blockTrail }) => ({
  label,
  value,
  blockTrail
});

export const formatFieldsWithAPIData = (
  afcDataList,
  defalutDataList,
  isFields = false
) => {
  if (
    !afcDataList ||
    afcDataList?.toString()?.toLowerCase().split(',').includes('any')
  ) {
    return isFields ? defalutDataList.map(pickFieldOption) : defalutDataList;
  }

  if (isFields) {
    const splittedData = afcDataList?.toString()?.split(',');

    return defalutDataList.reduce((acc, option) => {
      const { processingType, value, legacyName } = option;
      if (
        processingType === 'custom_json' ||
        (processingType === 'default' &&
          // FIN-13223 — `value` is now the canonical field id but these allowlists are seeded
          // with the legacy spelling, so match either or every renamed field drops out.
          (splittedData.includes(value) || splittedData.includes(legacyName)))
      ) {
        acc.push(pickFieldOption(option));
      }

      return acc;
    }, []);
  }

  const allowed = afcDataList?.toString()?.split(',') || [];

  // FIN-13223 — same both-spellings match as the isFields branch above.
  return defalutDataList.filter(
    (item) => allowed.includes(item.value) || allowed.includes(item.legacyName)
  );
};

/**
 * Group the flat option list by its v3 block trail, mirroring the rule-builder picker.
 * Returns [] when nothing carries a block, so the dropdown falls back to its flat list.
 */
export const groupFieldOptions = (options = []) => {
  const byPath = new Map();

  (options || []).forEach((option) => {
    const trail = option?.blockTrail;
    if (!trail || !trail.length) return;
    const label = trail.join(PATH_SEPARATOR);
    if (!byPath.has(label)) byPath.set(label, { trail, items: [] });
    byPath.get(label).items.push(option);
  });

  const order = (label) => (label === GENERAL_GROUP ? 1 : 0);

  return [...byPath.entries()]
    .map(([label, group]) => ({ label, ...group }))
    .sort(
      (a, b) =>
        order(a.label) - order(b.label) || a.label.localeCompare(b.label)
    );
};
